import { showHttpError, showSuccess } from '@calendly/alert'
import { parseQuery } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'
import { createAsyncThunk } from '@reduxjs/toolkit'

import { pushQueryParams } from 'actions/core/navigation'
import { upgradeSubscription } from 'components/billing/use_upgrade_subscription'

import { createUserGroupRelationships, removeRelationships, resendInvitation } from '../api'
import { ORG_INVITES_PAGE_TYPE } from './constants'
import { userGroupSelector } from './group_management/selectors'
import { fetchGroup } from './group_management/slice'
import { determinePageSlice, determinePaginationData, determineSortingData, fetchMethod } from './helpers'

// Page actions
export const updatePage = (page, pageType) => async (dispatch, getState) => {
  const { startDataLoad } = determinePageSlice(pageType).actions

  dispatch(startDataLoad())

  const state = getState()
  const { sortOrder, sortField } = determineSortingData(state, pageType)
  const queryParams = { page, sort_order: sortOrder, sort_field: sortField }

  dispatch(pushQueryParams(queryParams))

  const { id } = userGroupSelector(state)
  await dispatch(fetchMethod(pageType)({ groupId: id, page }))
}

export const initPage = (groupId, pageType) => async (dispatch, _) => {
  const { setFilter, setPage, setSortField, setSortOrder, startDataLoad, loaded } = determinePageSlice(pageType).actions
  const params = parseQuery(window.location.search)
  const page = params.page ? parseInt(params.page, 10) : 1

  dispatch(setFilter(params.filter))
  dispatch(setPage(page))

  if (params.sort_field) {
    dispatch(setSortField(params.sort_field))
  }
  if (params.sort_order) {
    dispatch(setSortOrder(params.sort_order))
  }

  dispatch(startDataLoad())

  const { error, payload: results } = await dispatch(fetchMethod(pageType)({ groupId, page }))

  if (error) return

  if (results.pagination.is_out_of_range) {
    await dispatch(updatePage(1, pageType))
  }

  dispatch(loaded())
}

export const refreshPage = (pageType) => async (dispatch, getState) => {
  const { currentPage } = determinePaginationData(getState(), pageType)
  await dispatch(updatePage(currentPage, pageType))
}

export const updateFilter = (pageType) => (filter) => (dispatch, getState) => {
  const state = getState()
  const params = parseQuery(window.location.search)
  const { filterUpdated, startDataLoad } = determinePageSlice(pageType).actions

  dispatch(startDataLoad())
  dispatch(pushQueryParams({ ...params, filter }))

  const { id } = userGroupSelector(state)

  dispatch(filterUpdated(filter))
  dispatch(fetchMethod(pageType)({ groupId: id, page: 1 }))
}

export const sortMembers = (field, pageType) => async (dispatch, getState) => {
  const { sortBy } = determinePageSlice(pageType).actions
  const params = parseQuery(window.location.search)

  dispatch(sortBy(field))

  const state = getState()
  const { sortOrder, sortField } = determineSortingData(state, pageType)
  dispatch(pushQueryParams({ ...params, sort_field: sortField, sort_order: sortOrder }))

  const { id } = userGroupSelector(state)
  dispatch(fetchMethod(pageType)({ groupId: id, page: 1 }))
}

// Thunks
export const addToGroup = createAsyncThunk(
  'groupRelationships/create',
  async ({
    groupId,
    organizationMembershipIds,
    organizationInvitationIds,
    organizationMigrationIds,
    orgAdminsToRemove,
    role
  }, { dispatch, _, rejectWithValue }) => {
    const [success, response] = await createUserGroupRelationships(
      groupId,
      {
        organization_membership_ids: organizationMembershipIds,
        organization_invitation_ids: organizationInvitationIds,
        organization_migration_ids: organizationMigrationIds,
        org_admins_to_remove: orgAdminsToRemove,
        role
      }
    )

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue(false)
    }

    dispatch(showSuccess(I18n.t('publisher.flash.saved')))
    dispatch(fetchGroup({ groupId }))

    return true
  }
)

export const removeGroupRelationships = (pageType) => createAsyncThunk(
  'groupRelationships/delete',
  async ({ groupId, usersToRemove, visibleUsers, role = null }, { dispatch, getState, rejectWithValue }) => {
    const { closeRemovePopup, clearSelection, setRemovePopupPending } = determinePageSlice(pageType).actions
    dispatch(setRemovePopupPending(true))

    const groupRelationshipIdsToRemove = usersToRemove
      .map((membership) => {
        const groupRelationships = membership.user_group_relationships[groupId]

        return role ? groupRelationships[role] : Object.values(groupRelationships)
      })
      .flat()
      .map((relationship) => relationship.relationship_id)

    const [success, response] = await removeRelationships(groupId, groupRelationshipIdsToRemove)

    if (!success) {
      dispatch(setRemovePopupPending(false))
      dispatch(showHttpError(response))
      return rejectWithValue(false)
    }

    const { currentPage, totalCount, pageSize } = determinePaginationData(getState(), pageType)
    const noUsersLeftOnPage = usersToRemove.length >= visibleUsers.length

    if (currentPage > 1 && noUsersLeftOnPage) {
      const pageToLandOn = Math.floor((totalCount - usersToRemove.length - 1) / pageSize) + 1
      await dispatch(updatePage(pageToLandOn, pageType))
    } else {
      await dispatch(refreshPage(pageType))
    }

    dispatch(closeRemovePopup())
    dispatch(clearSelection())
    dispatch(fetchGroup({ groupId }))
    dispatch(showSuccess(I18n.t('publisher.flash.saved')))

    return true
  }
)

export const reinviteMember = (member) => async (dispatch) => {
  const [success, response] = await resendInvitation(member.id)

  if (success) {
    dispatch(showSuccess(response.message))
  } else if (response.type === 'quantity_overflow') {
    const {
      invitations_quantity: invitationsQuantity,
      min_quantity: minQuantity
    } = response.payload

    upgradeSubscription({
      dispatch,
      featureKey: 'organization_invitation',
      skipPreface: true,
      minQuantity,
      invitationsQuantity
    })
  } else {
    dispatch(showHttpError(response))
  }

  dispatch(refreshPage(ORG_INVITES_PAGE_TYPE))
}
